/**
 * Client for the /api/me/collections + /api/u/{slug}/c/{collectionSlug}
 * endpoints (ADR-0015 Phase 1.2). Likes and Collections both ride the
 * catalog response for their per-package markers (likedByMe /
 * inAnyOfMyCollections); this module covers the write paths plus the
 * page-level reads (lists + detail) that the SPA needs.
 */
export type CollectionVisibility = 'Public' | 'Unlisted' | 'Private';

export interface CollectionResponse {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    visibility: CollectionVisibility;
    coverWorkId: string | null;
    memberCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface CollectionMemberResponse {
    workId: string;
    position: number;
    addedAt: string;
}

export interface CollectionDetailResponse extends Omit<CollectionResponse, 'memberCount'> {
    members: CollectionMemberResponse[];
}

export interface CollectionUpsertRequest {
    slug: string;
    name: string;
    description?: string | null;
    visibility?: CollectionVisibility | null;
    coverWorkId?: string | null;
}

const credentials: RequestInit = { credentials: 'include' };
const jsonHeaders = { 'Content-Type': 'application/json' };

export async function listMyCollections(): Promise<CollectionResponse[]> {
    const res = await fetch('/api/me/collections', credentials);
    if (!res.ok) throw new Error(`listMyCollections HTTP ${res.status}`);
    return (await res.json()) as CollectionResponse[];
}

export async function getMyCollection(slug: string): Promise<CollectionDetailResponse> {
    const res = await fetch(`/api/me/collections/${encodeURIComponent(slug)}`, credentials);
    if (!res.ok) throw new Error(`getMyCollection HTTP ${res.status}`);
    return (await res.json()) as CollectionDetailResponse;
}

export async function createCollection(body: CollectionUpsertRequest): Promise<CollectionResponse> {
    const res = await fetch('/api/me/collections', {
        ...credentials,
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`createCollection HTTP ${res.status}`);
    return (await res.json()) as CollectionResponse;
}

export async function updateCollection(
    slug: string,
    body: Partial<CollectionUpsertRequest>,
): Promise<CollectionResponse> {
    const res = await fetch(`/api/me/collections/${encodeURIComponent(slug)}`, {
        ...credentials,
        method: 'PUT',
        headers: jsonHeaders,
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`updateCollection HTTP ${res.status}`);
    return (await res.json()) as CollectionResponse;
}

export async function deleteCollection(slug: string): Promise<void> {
    const res = await fetch(`/api/me/collections/${encodeURIComponent(slug)}`, {
        ...credentials,
        method: 'DELETE',
    });
    if (!res.ok && res.status !== 204) throw new Error(`deleteCollection HTTP ${res.status}`);
}

/**
 * Slugs of caller-owned collections that already contain the given
 * work. One round trip; backs the "add to collection" popover.
 */
export async function listCollectionsContainingWork(
    workId: string,
): Promise<string[]> {
    const res = await fetch(
        `/api/me/collections/_membership/${encodeURIComponent(workId)}`,
        credentials,
    );
    if (!res.ok) throw new Error(`listCollectionsContainingWork HTTP ${res.status}`);
    return (await res.json()) as string[];
}

export async function addWorkToCollection(
    slug: string,
    workId: string,
    position?: number,
): Promise<CollectionMemberResponse> {
    const body = position !== undefined ? JSON.stringify({ position }) : '{}';
    const res = await fetch(
        `/api/me/collections/${encodeURIComponent(slug)}/works/${encodeURIComponent(workId)}`,
        {
            ...credentials,
            method: 'PUT',
            headers: jsonHeaders,
            body,
        },
    );
    if (!res.ok) throw new Error(`addWorkToCollection HTTP ${res.status}`);
    return (await res.json()) as CollectionMemberResponse;
}

export async function removeWorkFromCollection(
    slug: string,
    workId: string,
): Promise<void> {
    const res = await fetch(
        `/api/me/collections/${encodeURIComponent(slug)}/works/${encodeURIComponent(workId)}`,
        {
            ...credentials,
            method: 'DELETE',
        },
    );
    if (!res.ok && res.status !== 204) {
        throw new Error(`removeWorkFromCollection HTTP ${res.status}`);
    }
}

export async function listPublicCollections(slug: string): Promise<CollectionResponse[]> {
    const res = await fetch(`/api/u/${encodeURIComponent(slug)}/collections`, credentials);
    if (!res.ok) throw new Error(`listPublicCollections HTTP ${res.status}`);
    return (await res.json()) as CollectionResponse[];
}

export async function getPublicCollection(
    slug: string,
    collectionSlug: string,
): Promise<CollectionDetailResponse> {
    const res = await fetch(
        `/api/u/${encodeURIComponent(slug)}/c/${encodeURIComponent(collectionSlug)}`,
        credentials,
    );
    if (!res.ok) throw new Error(`getPublicCollection HTTP ${res.status}`);
    return (await res.json()) as CollectionDetailResponse;
}
